import React, { FunctionComponent } from 'react';
import { AbsoluteFill, Sequence } from 'remotion';
import {
  groupTracks,
  TrackGroup,
  TrackItem,
  TrackType,
} from '../remotion-tracks';
import Thumbnails from './thumbnails/thumbnails';
import { AssetDisplay } from '../remotion-assets/remotion-assets';
import RemotionTransition from '../remotion-transition/remotion-transition';
import { AudioTrackPlayer } from '../remotion-audio/remotion-audio';
import { AudioTrack } from '../remotion-types';
import RemotionVoiceover from '../remotion-voiceover/remotion-voiceover';
import { orderBy } from 'lodash';

const TextOverlay: FunctionComponent<{
  item: TrackItem;
}> = ({ item }) => {
  const { top, left, width, height, data } = item;

  return (
    <div
      style={{
        position: 'absolute',
        left: left ?? '50%',
        top: top ?? '50%',
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '100px',
        transform: left || top ? 'none' : 'translate(-50%, -50%)',
      }}
    >
      {data.text}
    </div>
  );
};

export const renderContentByType = ({
  trackItem,
  index,
  absoluteFrame,
  isRtl,
}: {
  isRtl: boolean;
  trackItem: TrackItem;
  index: number;
  absoluteFrame: number;
}): React.ReactNode => {
  const { top, left, width, height } = trackItem;

  let component: any = null;

  if (trackItem.type === TrackType.Text) {
    component = <TextOverlay item={trackItem} />;
  }
  if (trackItem.type === TrackType.Thumbnail) {
    component = <Thumbnails item={trackItem} />;
  }
  if (trackItem.type === TrackType.Audio) {
    component = <AudioTrackPlayer track={trackItem.data as AudioTrack} />;
  }
  if (trackItem.type === TrackType.Voiceover) {
    component = (
      <RemotionVoiceover
        isRtl={isRtl}
        absoluteFrame={absoluteFrame}
        item={trackItem}
        index={index}
      />
    );
  }
  if (
    trackItem.type === TrackType.Image ||
    trackItem.type === TrackType.Video
  ) {
    component = (
      <AssetDisplay
        durationInFrames={trackItem.durationInFrames}
        index={index}
        asset={trackItem.data}
      />
    );
  }

  // Add other types as needed:
  // if (track.type === OverlayTrackType.Image) {
  //   return <ImageOverlay item={track} />;
  // }

  // if (track.type === OverlayTrackType.Video) {
  //   return <VideoOverlay item={track} />;
  // }

  return (
    <AbsoluteFill
      style={{
        top,
        height,
        left,
        width,
      }}
    >
      {component}
    </AbsoluteFill>
  );
};

const RemotionTracks: FunctionComponent<{
  groups: TrackGroup[];
  isRtl: boolean;
  absoluteFrame: number;
}> = ({ groups, absoluteFrame, isRtl }) => {
  const sortedGroups = orderBy(groups, 'index', 'asc');

  return (
    <>
      {sortedGroups.map((group) => {
        const isAssetGroup = group.type === 'asset';

        if (isAssetGroup) {
          return (
            <RemotionTransition
              key={group.index}
              tracks={group.tracks}
              isRtl={isRtl}
              absoluteFrame={absoluteFrame}
            />
          );
        }

        return group.tracks.map((track, index) => (
          <Sequence
            //@ts-expect-error zubi
            premountFor={60}
            key={`${group.index}-${index}`}
            from={track.from}
            durationInFrames={track.durationInFrames}
            layout="none"
          >
            {renderContentByType({
              trackItem: track,
              index,
              isRtl,
              absoluteFrame,
            })}
          </Sequence>
        ));
      })}
    </>
  );
};

export default RemotionTracks;
